/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskPoliciesForMcpServerRequest,
  ListRiskPoliciesForMcpServerSecurity,
} from "../models/operations/listriskpoliciesformcpserver.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListPoliciesForMcpServerQuery,
  prefetchRiskListPoliciesForMcpServer,
  queryKeyRiskListPoliciesForMcpServer,
  RiskListPoliciesForMcpServerQueryData,
} from "./riskListPoliciesForMcpServer.core.js";
export {
  buildRiskListPoliciesForMcpServerQuery,
  prefetchRiskListPoliciesForMcpServer,
  queryKeyRiskListPoliciesForMcpServer,
  type RiskListPoliciesForMcpServerQueryData,
};

export type RiskListPoliciesForMcpServerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskPoliciesForMcpServer risk
 *
 * @remarks
 * List enabled MCP-scoped risk policies that apply to an MCP server and optional tool. Policies without an MCP scope are excluded.
 */
export function useRiskListPoliciesForMcpServer(
  request: ListRiskPoliciesForMcpServerRequest,
  security?: ListRiskPoliciesForMcpServerSecurity | undefined,
  options?: QueryHookOptions<
    RiskListPoliciesForMcpServerQueryData,
    RiskListPoliciesForMcpServerQueryError
  >,
): UseQueryResult<
  RiskListPoliciesForMcpServerQueryData,
  RiskListPoliciesForMcpServerQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListPoliciesForMcpServerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskPoliciesForMcpServer risk
 *
 * @remarks
 * List enabled MCP-scoped risk policies that apply to an MCP server and optional tool. Policies without an MCP scope are excluded.
 */
export function useRiskListPoliciesForMcpServerSuspense(
  request: ListRiskPoliciesForMcpServerRequest,
  security?: ListRiskPoliciesForMcpServerSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListPoliciesForMcpServerQueryData,
    RiskListPoliciesForMcpServerQueryError
  >,
): UseSuspenseQueryResult<
  RiskListPoliciesForMcpServerQueryData,
  RiskListPoliciesForMcpServerQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListPoliciesForMcpServerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListPoliciesForMcpServerData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId: string;
      toolName?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListPoliciesForMcpServerQueryData,
): RiskListPoliciesForMcpServerQueryData | undefined {
  const key = queryKeyRiskListPoliciesForMcpServer(...queryKeyBase);

  return client.setQueryData<RiskListPoliciesForMcpServerQueryData>(key, data);
}

export function invalidateRiskListPoliciesForMcpServer(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId: string;
      toolName?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "policies", "listForMcpServer", ...queryKeyBase],
  });
}

export function invalidateAllRiskListPoliciesForMcpServer(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "policies", "listForMcpServer"],
  });
}
